// One place that knows how to talk to this app's API.
//
// Every fetch() in this app used to look like:
//
//     const res = await fetch("/file/ready", { method: "POST" });
//     const result = await res.json();
//     if (result.success) { ... }
//
// with no res.ok check and no try/catch. Three separate problems came from
// that, all recorded in docs/AUDIT-2026-07.md:
//
//   * A signed-out user got a 302 to the HTML login page, which fetch follows
//     transparently, so `await res.json()` threw a SyntaxError in a promise
//     nobody caught and the user saw *nothing at all* (P1-8). The server
//     answers 401 JSON now, but the client still has to act on it.
//   * The three list pages only set `loading = false` inside the success
//     branch, so any failure left a permanent "Loading" spinner (P1-9).
//   * A network error or a 500 was indistinguishable from a successful
//     response with success:false.

/** Sends the browser to the login page, preserving nothing -- there is no
 *  deep-link-after-login flow in this app and inventing one here would be
 *  scope creep. */
function redirectToLogin() {
  window.location.href = "/login";
}

/**
 * Calls the API and returns the parsed body.
 *
 * @throws {Error} with a human-readable `message` on any failure. Callers
 *   should catch and show it; they no longer need to inspect status codes.
 */
export async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, {
      // Asking for JSON explicitly is what makes the server answer 401 with a
      // body instead of redirecting us to HTML. See helpers/auth.js wantsHtml.
      headers: { Accept: "application/json", ...(options.headers || {}) },
      ...options,
    });
  } catch (err) {
    // fetch only rejects on a network-level failure.
    throw new Error(`Could not reach the server (${err.message})`);
  }

  // A 401 means "your session is gone" everywhere except on the login page,
  // where it means "wrong password". Without this guard, submitting a bad
  // password would navigate to /login -- reloading the page and destroying the
  // error message the user needs to see.
  if (res.status === 401 && window.location.pathname !== "/login") {
    redirectToLogin();
    throw new Error("Signed out");
  }

  let body = null;
  try {
    body = await res.json();
  } catch {
    // A non-JSON body on an error status is not worth reporting verbatim; on a
    // success status it means the endpoint is not what we thought it was.
    if (res.ok) throw new Error("The server sent a malformed response");
  }

  if (!res.ok) {
    throw new Error((body && body.message) || `Request failed (${res.status})`);
  }

  // Some endpoints answer 200 with {success:false}. Treat that as a failure
  // too, so callers have exactly one thing to check.
  if (body && body.success === false) {
    throw new Error(body.message || "Request failed");
  }

  return body;
}
